import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, FolderOpen, Files, Check, ChevronRight } from 'lucide-react';
import type { CreateStep } from '@/lib/types';

interface ProgressStepsProps {
  currentStep: CreateStep;
  onStepClick?: (step: CreateStep) => void;
}

const STEP_ORDER = ['capture', 'project', 'template'] as const;

const STEP_CONFIG = [
  { key: 'capture', i18nKey: 'create.stepCapture', icon: Sparkles },
  { key: 'project', i18nKey: 'create.stepProject', icon: FolderOpen },
  { key: 'template', i18nKey: 'create.stepDocuments', icon: Files },
] as const;

export function ProgressSteps({ currentStep, onStepClick }: ProgressStepsProps) {
  const { t } = useTranslation();
  const currentStepIndex = STEP_ORDER.indexOf(currentStep as typeof STEP_ORDER[number]);

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 mb-6">
      {STEP_CONFIG.map((s, index) => {
        const thisStepIndex = STEP_ORDER.indexOf(s.key);
        const isActive = currentStep === s.key;
        const isCompleted = currentStepIndex > thisStepIndex;
        const isClickable = isCompleted && onStepClick;
        const Icon = s.icon;

        return (
          <React.Fragment key={s.key}>
            <button
              type="button"
              disabled={!isClickable}
              onClick={() => isClickable && onStepClick(s.key as CreateStep)}
              className={`
                flex items-center gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all
                ${isActive
                  ? 'bg-indigo-100 text-indigo-700 border border-indigo-300 shadow-sm'
                  : isCompleted
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 cursor-pointer'
                    : 'bg-slate-100 text-slate-400 border border-slate-200'
                }
                ${!isClickable ? 'cursor-default' : ''}
              `}
            >
              {isCompleted ? (
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              ) : (
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              )}
              {t(s.i18nKey)}
            </button>
            {index < STEP_CONFIG.length - 1 && (
              <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
